import React, { useState, useEffect } from 'react';
import { BRAND } from '../config/branding';

/**
 * Splash Screen PWA pour Cabinet 4.0
 * S'affiche au démarrage de l'application en mode PWA
 */

const SplashScreen = ({ 
  minDisplayTime = 2500, 
  onComplete,
  version = '4.0'
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Démarrer l'animation d'entrée
    const animationTimer = setTimeout(() => {
      setIsAnimating(true);
    }, 100);

    // Timer minimum d'affichage
    const displayTimer = setTimeout(() => {
      setIsVisible(false);
    }, minDisplayTime);

    return () => {
      clearTimeout(animationTimer);
      clearTimeout(displayTimer);
    };
  }, [minDisplayTime]);

  // Appeler onComplete après la transition de sortie
  useEffect(() => {
    if (!isVisible) {
      const exitTimer = setTimeout(() => {
        onComplete?.();
      }, 500); // Durée de la transition de sortie
      return () => clearTimeout(exitTimer);
    }
  }, [isVisible, onComplete]);

  if (!isVisible && !isAnimating) return null;

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden transition-opacity duration-500 ${
        isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      style={{
        background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)'
      }}
    >
      {/* Cercles décoratifs en arrière-plan */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute -top-1/4 -right-1/4 w-1/2 h-1/2 rounded-full opacity-5"
          style={{ background: 'radial-gradient(circle, #B69D74 0%, transparent 70%)' }}
        />
        <div 
          className="absolute -bottom-1/4 -left-1/4 w-1/2 h-1/2 rounded-full opacity-5"
          style={{ background: 'radial-gradient(circle, #B69D74 0%, transparent 70%)' }}
        />
      </div>

      {/* Contenu principal */}
      <div className="relative flex flex-col items-center justify-center px-8">
        {/* Logo */}
        <div 
          className={`transform transition-all duration-1000 ease-out ${
            isAnimating ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 scale-90'
          }`}
        >
          <img 
            src="/logo.png" 
            alt={BRAND.name}
            className="w-40 h-40 sm:w-48 sm:h-48 md:w-56 md:h-56 object-contain drop-shadow-2xl"
            style={{
              filter: 'drop-shadow(0 20px 40px rgba(182, 157, 116, 0.3))'
            }}
          />
        </div>

        {/* Tagline */}
        <p 
          className={`mt-10 text-center text-lg sm:text-xl md:text-2xl font-light tracking-wide leading-relaxed max-w-sm transform transition-all duration-1000 delay-300 ease-out ${
            isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}
          style={{ color: 'rgba(255, 255, 255, 0.9)' }}
        >
          Un outil pour les{' '}
          <span className="font-medium" style={{ color: '#B69D74' }}>avocats</span>,
          <br />
          créé par des{' '}
          <span className="font-medium" style={{ color: '#B69D74' }}>avocats</span>.
        </p>

        {/* Points de chargement animés */}
        <div 
          className={`flex gap-2 mt-12 transform transition-all duration-700 delay-700 ${
            isAnimating ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <span 
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ backgroundColor: '#B69D74', animationDelay: '0ms' }}
          />
          <span 
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ backgroundColor: '#B69D74', animationDelay: '200ms' }}
          />
          <span 
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ backgroundColor: '#B69D74', animationDelay: '400ms' }}
          />
        </div>
      </div>

      {/* Version en bas */}
      <span 
        className={`absolute bottom-8 text-xs tracking-widest uppercase transform transition-all duration-700 delay-1000 ${
          isAnimating ? 'opacity-40' : 'opacity-0'
        }`}
        style={{ color: 'rgba(255, 255, 255, 0.4)' }}
      >
        Cabinet {version}
      </span>
    </div>
  );
};

export default SplashScreen;
